import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminButton, AdminCard, AdminCardHeader, AdminErrorBanner, AdminInfoRow, AdminLoadingState, AdminPageLayout, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import { fetchAdminLocationDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { AdminLocationDetail, AdminLocationItem } from "@/types.ts";
import type { ReactNode } from "react";

const formatCoordinate = (location: Pick<AdminLocationItem, "latitude" | "longitude"> | null) => {
  if (!location || location.latitude == null || location.longitude == null) return "-";
  return `${Number(location.latitude).toFixed(6)}, ${Number(location.longitude).toFixed(6)}`;
};

const getAddressLabel = (location: AdminLocationItem | null) => {
  if (!location) return "-";
  return location.addressDisplayLabel || location.addressText || [location.sido, location.sigungu].filter(Boolean).join(" ") || "-";
};

const getMapsUrl = (location: Pick<AdminLocationItem, "latitude" | "longitude"> | null) => {
  if (!location || location.latitude == null || location.longitude == null) return null;
  return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
};

const SectionCard = ({ title, icon, children }: { title: string; icon: string; children: ReactNode }) => {
  return (
    <AdminCard>
      <AdminCardHeader title={title} icon={icon} />
      {children}
    </AdminCard>
  );
};

const LocationDetailPage = () => {
  const { seniorUserAccountId } = useParams<{ seniorUserAccountId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminLocationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const parsedSeniorUserAccountId = Number(seniorUserAccountId);
  const latestLocation = detail?.latestLocation ?? null;
  const mapsUrl = getMapsUrl(latestLocation);

  useEffect(() => {
    let ignore = false;
    const loadDetail = async () => {
      if (!Number.isFinite(parsedSeniorUserAccountId)) {
        setErrorMessage("잘못된 위치 대상 ID입니다.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setErrorMessage(null);
      try {
        const data = await fetchAdminLocationDetail(parsedSeniorUserAccountId);
        if (!ignore) setDetail(data);
      } catch (error) {
        if (!ignore) {
          setDetail(null);
          setErrorMessage(error instanceof Error ? error.message : "위치 상세 정보를 불러오지 못했습니다.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadDetail();
    return () => {
      ignore = true;
    };
  }, [parsedSeniorUserAccountId]);

  const titleName = detail?.elderlyName ?? "위치 대상";

  return (
    <AdminPageLayout
            title="위치 관제 상세"
            subtitle={`${titleName} · 최근 위치와 위치 이력`}
            actions={
              <AdminButton type="button" variant="secondary" size="sm" onClick={() => navigate("/admin/location")} icon="ri-arrow-left-s-line">
                목록으로
              </AdminButton>
            }
          >
          <div className="mt-4 mb-3 flex items-center gap-1.5 text-xs text-slate-400">
            <span>관리자 콘솔</span>
            <i className="ri-arrow-right-s-line text-xs" />
            <AdminButton variant="ghost" size="sm" className="-mx-2.5" onClick={() => navigate("/admin/location")}>
              위치 관제
            </AdminButton>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-slate-600 font-medium">{titleName} 상세</span>
          </div>

          <div className="mb-4">
            <AdminErrorBanner message={errorMessage} />
          </div>

          {loading ? (
            <AdminLoadingState message="위치 상세 정보를 불러오는 중입니다." />
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2 flex flex-col gap-4">
                <SectionCard title="최근 위치" icon="ri-map-pin-line">
                  <AdminInfoRow bordered label="Senior" value={detail?.elderlyName} />
                  <AdminInfoRow bordered label="기기" value={latestLocation?.deviceName || "-"} />
                  <AdminInfoRow bordered label="주소" value={getAddressLabel(latestLocation)} />
                  <AdminInfoRow bordered label="좌표" value={formatCoordinate(latestLocation)} />
                  <AdminInfoRow bordered label="정확도" value={latestLocation?.accuracyMeter == null ? "-" : `${Math.round(Number(latestLocation.accuracyMeter))}m`} />
                  <AdminInfoRow bordered label="수집 방식" value={latestLocation?.source || "-"} />
                  <AdminInfoRow bordered label="권한 상태" value={latestLocation?.permissionStatus || "-"} />
                  <AdminInfoRow bordered label="위치 제공자" value={latestLocation?.providerStatus || "-"} />
                  <AdminInfoRow bordered label="수신 시각" value={formatDateTime(latestLocation?.collectedAt ?? null)} />
                  {mapsUrl && (
                    <div className="px-4 py-3">
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100"
                      >
                        <i className="ri-map-2-line text-xs" />
                        지도에서 열기
                      </a>
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="최근 위치 이력" icon="ri-route-line">
                  <AdminTable
                    headers={["수신 시각", "주소", "좌표", "정확도", "권한", "제공자"]}
                    emptyMessage={detail?.history.length === 0 ? "위치 이력이 없습니다." : undefined}
                  >
                    {detail?.history.map((log) => (
                      <AdminTr key={log.locationLogId}>
                        <AdminTd className="text-xs text-slate-500">{formatDateTime(log.collectedAt)}</AdminTd>
                        <AdminTd className="text-xs text-slate-700">{log.addressDisplayLabel || log.addressText || "-"}</AdminTd>
                        <AdminTd className="text-xs text-slate-500">{formatCoordinate(log)}</AdminTd>
                        <AdminTd className="text-xs text-slate-500">{log.accuracyMeter == null ? "-" : `${Math.round(Number(log.accuracyMeter))}m`}</AdminTd>
                        <AdminTd className="text-xs text-slate-500">{log.permissionStatus}</AdminTd>
                        <AdminTd className="text-xs text-slate-500">{log.providerStatus}</AdminTd>
                      </AdminTr>
                    ))}
                  </AdminTable>
                </SectionCard>
              </div>

              <div className="xl:col-span-1 flex flex-col gap-4">
                <SectionCard title="중요 장소" icon="ri-map-pin-2-line">
                  {detail?.importantPlaces.length === 0 && (
                    <div className="px-4 py-8 text-center text-xs text-slate-400">등록된 중요 장소가 없습니다.</div>
                  )}
                  <div className="divide-y divide-slate-100">
                    {detail?.importantPlaces.map((place) => (
                      <div key={place.importantPlaceId} className="px-4 py-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-slate-800">{place.placeName}</span>
                          <span className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">
                            {place.placeType}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{place.addressText || formatCoordinate(place)}</p>
                        <p className="mt-1 text-xs text-slate-400">반경 {place.radiusMeter}m</p>
                        {place.memo && <p className="mt-2 text-xs text-slate-500">{place.memo}</p>}
                      </div>
                    ))}
                  </div>
                </SectionCard>

                <SectionCard title="바로가기" icon="ri-links-line">
                  <div className="p-4 grid gap-2">
                    <AdminButton
                      type="button"
                      onClick={() => navigate(`/admin/users/seniors/${parsedSeniorUserAccountId}`)}
                      variant="secondary"
                      size="md"
                      icon="ri-user-heart-line"
                    >
                      Senior 통합 상세
                    </AdminButton>
                    <AdminButton
                      type="button"
                      onClick={() => navigate(`/admin/users/seniors/${parsedSeniorUserAccountId}?tab=care`)}
                      variant="secondary"
                      size="md"
                      icon="ri-heart-pulse-line"
                    >
                      생활신호 보기
                    </AdminButton>
                    <AdminButton
                      type="button"
                      onClick={() => navigate(`/admin/devices?seniorUserAccountId=${parsedSeniorUserAccountId}${latestLocation?.deviceId ? `&deviceId=${latestLocation.deviceId}` : ""}`)}
                      variant="soft"
                      size="md"
                      icon="ri-smartphone-line"
                    >
                      기기 관리로 이동
                    </AdminButton>
                  </div>
                </SectionCard>
              </div>
            </div>
          )}
    </AdminPageLayout>
  );
};

export default LocationDetailPage;
